"use client";

import React, { useState, useEffect, useCallback } from "react";
import { FileText, Zap, Loader2, AlertCircle } from "lucide-react";
import { getAttributeString } from "@/lib/utils/dynamodb";
import { ReportData } from "./types";
import { fetchAllStructuredInfo } from "@/lib/clients/structured-info";
import { fetchAllPrompts } from "@/lib/clients/prompts";
import { getStructureDefinitionId } from "./utils";

interface ReportHeaderTagsProps {
  reportData: ReportData;
  className?: string;
}

interface TagData {
  type: "format" | "prompt";
  label: string;
  icon: React.ReactNode;
  variant: "primary" | "secondary" | "accent";
}

export const ReportHeaderTags: React.FC<ReportHeaderTagsProps> = ({
  reportData,
  className = ""
}) => {
  console.log('🚀 [ReportHeaderTags] コンポーネントがマウントされました！');
  console.log('🚀 [ReportHeaderTags] 受信したprops:', { reportData, className });
  
  const [tags, setTags] = useState<TagData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  console.log('🚀 [ReportHeaderTags] 初期state設定完了:', { tags: tags.length, isLoading, error });

  // 🔄 localStorageからメタデータを取得する関数
  const getReportMetadataFromStorage = useCallback((employeeID: string, reportID: string) => {
    try {
      const metadataKey = `report-metadata-${employeeID}-${reportID}`;
      const storedData = localStorage.getItem(metadataKey);
      
      if (storedData) {
        const metadata = JSON.parse(storedData);
        console.log('🔄 [ReportHeaderTags] localStorage からメタデータを復元:', {
          key: metadataKey,
          metadata
        });
        return metadata;
      } else {
        console.log('🔄 [ReportHeaderTags] localStorage にメタデータが見つかりません:', metadataKey);
        return null;
      }
    } catch (error) {
      console.error('🔄 [ReportHeaderTags] localStorage からのメタデータ取得に失敗:', error);
      return null;
    }
  }, []);

  // 🧹 古いlocalStorageエントリをクリーンアップする関数
  const cleanupOldMetadata = useCallback(() => {
    try {
      const keysToCheck = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('report-metadata-')) {
          keysToCheck.push(key);
        }
      }

      const currentTime = Date.now();
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7日間
      let cleanedCount = 0;

      keysToCheck.forEach(key => {
        try {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            const metadata = JSON.parse(storedData);
            const dataTimestamp = metadata.timestamp || 0;
            
            if (currentTime - dataTimestamp > sevenDaysInMs) {
              localStorage.removeItem(key);
              cleanedCount++;
              console.log('🧹 [ReportHeaderTags] 古いメタデータを削除:', key);
            }
          }
        } catch (error) {
          console.warn('🧹 [ReportHeaderTags] メタデータクリーンアップ中にエラー:', key, error);
          // 破損したデータも削除
          localStorage.removeItem(key);
          cleanedCount++;
        }
      });

      if (cleanedCount > 0) {
        console.log('🧹 [ReportHeaderTags] localStorage クリーンアップ完了:', `${cleanedCount}件削除`);
      }
    } catch (error) {
      console.error('🧹 [ReportHeaderTags] localStorage クリーンアップに失敗:', error);
    }
  }, []);

  // フォーマット名を取得する関数
  const fetchFormatName = async (formatId: string): Promise<string | null> => {
    console.log('[ReportHeaderTags] fetchFormatName 開始, formatId:', formatId);
    try {
      console.log('[ReportHeaderTags] fetchAllStructuredInfo を呼び出し中...');
      const structuredInfoList = await fetchAllStructuredInfo();
      console.log('[ReportHeaderTags] fetchAllStructuredInfo 結果:', structuredInfoList);
      console.log('[ReportHeaderTags] 構造化情報リスト件数:', structuredInfoList.length);
      
      const formatInfo = structuredInfoList.find(info => info.id === formatId);
      console.log('[ReportHeaderTags] 該当フォーマット情報:', formatInfo);
      
      const result = formatInfo?.name || null;
      console.log('[ReportHeaderTags] fetchFormatName 結果:', result);
      return result;
    } catch (error) {
      console.error('[ReportHeaderTags] フォーマット名の取得に失敗:', error);
      return null;
    }
  };

  // カスタムプロンプト名を取得する関数
  const fetchCustomPromptName = async (promptId: string): Promise<string | null> => {
    console.log('[ReportHeaderTags] fetchCustomPromptName 開始, promptId:', promptId);
    try {
      console.log('[ReportHeaderTags] fetchAllPrompts を呼び出し中...');
      const prompts = await fetchAllPrompts();
      console.log('[ReportHeaderTags] fetchAllPrompts 結果:', prompts);
      console.log('[ReportHeaderTags] プロンプトリスト件数:', prompts.length);
      
      const prompt = prompts.find(p => p.id === promptId);
      console.log('[ReportHeaderTags] 該当プロンプト情報:', prompt);
      
      const result = prompt?.prompt_name || null;
      console.log('[ReportHeaderTags] fetchCustomPromptName 結果:', result);
      return result;
    } catch (error) {
      console.error('[ReportHeaderTags] カスタムプロンプト名の取得に失敗:', error);
      return null;
    }
  };

  // カスタムプロンプト情報を取得する関数
  const getCustomPromptInfo = useCallback(async (): Promise<{ hasPrompt: boolean; promptName?: string; promptContent?: string; isFromLibrary?: boolean }> => {
    console.log('[ReportHeaderTags] getCustomPromptInfo 開始');
    const report = reportData?.report;
    console.log('[ReportHeaderTags] report データ:', JSON.stringify(report, null, 2));
    
    if (!report) {
      console.log('[ReportHeaderTags] report が存在しないため、hasPrompt: false を返します');
      return { hasPrompt: false };
    }
    
    // カスタムプロンプトライブラリから選択されている場合
    console.log('[ReportHeaderTags] custompromptid を確認中...');
    console.log('[ReportHeaderTags] report.custompromptid:', report.custompromptid);
    const customPromptId = getAttributeString(report.custompromptid);
    console.log('[ReportHeaderTags] getAttributeString(custompromptid) 結果:', customPromptId);
    
    if (customPromptId) {
      console.log('[ReportHeaderTags] カスタムプロンプトIDが見つかりました、プロンプト名を取得中...', customPromptId);
      const promptName = await fetchCustomPromptName(customPromptId);
      console.log('[ReportHeaderTags] fetchCustomPromptName 結果:', promptName);
      
      const result = {
        hasPrompt: true,
        isFromLibrary: true,
        promptName: promptName || `プロンプトID: ${customPromptId.substring(0, 8)}...`
      };
      console.log('[ReportHeaderTags] ライブラリプロンプト結果:', result);
      return result;
    }
    
    // 直接テキストが入力されている場合
    console.log('[ReportHeaderTags] customprompttext を確認中...');
    console.log('[ReportHeaderTags] report.customprompttext:', report.customprompttext);
    const customPromptText = getAttributeString(report.customprompttext);
    console.log('[ReportHeaderTags] getAttributeString(customprompttext) 結果:', customPromptText);
    
    if (customPromptText && customPromptText.trim()) {
      console.log('[ReportHeaderTags] カスタムプロンプトテキストが見つかりました:', customPromptText);
      const result = {
        hasPrompt: true,
        isFromLibrary: false,
        promptContent: customPromptText
      };
      console.log('[ReportHeaderTags] 直接入力プロンプト結果:', result);
      return result;
    }
    
    console.log('[ReportHeaderTags] カスタムプロンプトが見つかりませんでした');
    console.log('[ReportHeaderTags] report の全キー:', Object.keys(report || {}));
    
    // 🔄 カスタムプロンプトが見つからない場合、localStorageから代替取得
    console.log('[ReportHeaderTags] localStorage からカスタムプロンプト代替取得を試行');
    const employeeID = reportData?.employeeID || reportData?.userId;
    const reportID = reportData?.reportID || reportData?.reportId;
    
    if (employeeID && reportID) {
      const storedMetadata = getReportMetadataFromStorage(employeeID, reportID);
      if (storedMetadata?.customPrompt) {
        console.log('[ReportHeaderTags] localStorage からカスタムプロンプト復元成功:', storedMetadata.customPrompt);
        return {
          hasPrompt: true,
          isFromLibrary: false,
          promptContent: storedMetadata.customPrompt
        };
      }
    }
    
    return { hasPrompt: false };
  }, [reportData, getReportMetadataFromStorage]);

  // クリーンアップとタグデータを構築
  useEffect(() => {
    // 🧹 コンポーネントマウント時にクリーンアップを実行
    cleanupOldMetadata();
    
    const buildTags = async () => {
      console.log('[ReportHeaderTags] デバッグ: buildTags 開始');
      console.log('[ReportHeaderTags] reportData:', JSON.stringify(reportData, null, 2));
      
      setIsLoading(true);
      setError(null);
      const newTags: TagData[] = [];

      try {
        // フォーマットタグの処理 - 直接渡されたstructure_definition_idを優先的に使用
        console.log('[ReportHeaderTags] structure_definition_id 確認開始');
        let formatId = reportData?.structure_definition_id || null;
        console.log('[ReportHeaderTags] 直接渡されたstructure_definition_id:', formatId);
        
        // 直接渡されたIDがない場合は、getStructureDefinitionIdを試行
        if (!formatId) {
          console.log('[ReportHeaderTags] getStructureDefinitionId 呼び出し開始');
          formatId = getStructureDefinitionId(reportData);
          console.log('[ReportHeaderTags] getStructureDefinitionId 取得結果:', formatId);
        }
        
        // 🔄 formatId が null の場合、localStorageから代替取得
        if (!formatId) {
          console.log('[ReportHeaderTags] formatId が null のため、localStorage から代替取得を試行');
          const employeeID = reportData?.employeeID || reportData?.userId;
          const reportID = reportData?.reportID || reportData?.reportId;
          
          if (employeeID && reportID) {
            const storedMetadata = getReportMetadataFromStorage(employeeID, reportID);
            if (storedMetadata?.structureDefinitionId) {
              formatId = storedMetadata.structureDefinitionId;
              console.log('[ReportHeaderTags] localStorage からフォーマットID復元成功:', formatId);
            }
          }
        }
        
        if (formatId) {
          console.log('[ReportHeaderTags] フォーマット名を取得中...', formatId);
          const formatName = await fetchFormatName(formatId);
          console.log('[ReportHeaderTags] フォーマット名取得結果:', formatName);
          
          if (formatName) {
            console.log('[ReportHeaderTags] フォーマットタグを追加:', formatName);
            newTags.push({
              type: "format",
              label: formatName,
              icon: <FileText className="h-3 w-3 text-gray-600 dark:text-gray-400" />,
              variant: "primary"
            });
          } else {
            // フォーマット名が取得できない場合はIDを表示
            console.log('[ReportHeaderTags] フォーマット名が取得できないため、IDを表示');
            newTags.push({
              type: "format",
              label: `フォーマット: ${formatId.substring(0, 8)}...`,
              icon: <FileText className="h-3 w-3 text-gray-500 dark:text-gray-500" />,
              variant: "secondary"
            });
          }
        } else {
          console.log('[ReportHeaderTags] formatId が null のため、フォーマットタグは追加されません');
        }

        // カスタムプロンプトタグの処理
        console.log('[ReportHeaderTags] カスタムプロンプト情報を取得中...');
        const promptInfo = await getCustomPromptInfo();
        console.log('[ReportHeaderTags] プロンプト情報取得結果:', promptInfo);
        
        if (promptInfo.hasPrompt) {
          let promptLabel = "";
          
          if (promptInfo.isFromLibrary && promptInfo.promptName) {
            // ライブラリから選択された場合はプロンプト名を表示
            promptLabel = promptInfo.promptName;
            console.log('[ReportHeaderTags] ライブラリプロンプト名:', promptLabel);
          } else if (promptInfo.promptContent) {
            // 直接入力の場合は内容の冒頭12文字程度を表示（ユーザー要求に合わせて12文字に変更）
            promptLabel = promptInfo.promptContent.length > 12 
              ? `${promptInfo.promptContent.substring(0, 12)}...`
              : promptInfo.promptContent;
            console.log('[ReportHeaderTags] 直接入力プロンプト内容:', promptLabel);
          } else {
            promptLabel = "カスタムプロンプト";
            console.log('[ReportHeaderTags] デフォルトプロンプトラベル:', promptLabel);
          }

          console.log('[ReportHeaderTags] プロンプトタグを追加:', promptLabel);
          newTags.push({
            type: "prompt",
            label: promptLabel,
            icon: <Zap className="h-3 w-3 text-gray-500 dark:text-gray-600" />,
            variant: "accent"
          });
        } else {
          console.log('[ReportHeaderTags] カスタムプロンプトが存在しないため、プロンプトタグは追加されません');
        }

        console.log('[ReportHeaderTags] 最終的なタグ配列:', newTags);
        console.log('[ReportHeaderTags] タグ数:', newTags.length);
        setTags(newTags);
      } catch (err) {
        console.error('[ReportHeaderTags] タグデータの構築に失敗:', err);
        setError('タグ情報の取得に失敗しました');
      } finally {
        setIsLoading(false);
        console.log('[ReportHeaderTags] buildTags 完了');
      }
    };

    buildTags();
  }, [reportData, getCustomPromptInfo, cleanupOldMetadata, getReportMetadataFromStorage]);

  // タグの色を取得（モノクロデザインシステム）
  const getTagColors = (variant: TagData['variant']) => {
    switch (variant) {
      case "primary":
        // フォーマットタグ: より濃いグレー
        return "bg-gray-200/90 dark:bg-gray-700/90 text-gray-800 dark:text-gray-200 border-gray-300/60 dark:border-gray-600/60";
      case "secondary":
        // セカンダリタグ: 中間グレー
        return "bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50";
      case "accent":
        // カスタムプロンプトタグ: ライトグレー
        return "bg-gray-50/90 dark:bg-gray-900/90 text-gray-600 dark:text-gray-400 border-gray-200/40 dark:border-gray-800/60";
      default:
        return "bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50";
    }
  };

  // ロード中の表示
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100/50 dark:bg-gray-800/50 border border-gray-200/30 dark:border-gray-700/30">
          <Loader2 className="h-3 w-3 animate-spin text-gray-500 dark:text-gray-400" />
          <span className="text-xs text-gray-500 dark:text-gray-400">読み込み中...</span>
        </div>
      </div>
    );
  }

  // エラー時の表示
  if (error) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-100/50 dark:bg-red-900/30 border border-red-200/30 dark:border-red-700/30">
          <AlertCircle className="h-3 w-3 text-red-500 dark:text-red-400" />
          <span className="text-xs text-red-600 dark:text-red-400">タグ取得失敗</span>
        </div>
      </div>
    );
  }

  // タグが存在しない場合は何も表示しない
  if (tags.length === 0) {
    console.log('[ReportHeaderTags] タグが0件のため表示しません');
    return null;
  }

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {tags.map((tag, index) => (
        <div
          key={`${tag.type}-${index}`}
          className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-full 
            border backdrop-blur-sm text-xs font-medium
            transition-all duration-200 hover:scale-105
            ${getTagColors(tag.variant)}
          `}
          style={{
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          title={`${tag.type === 'format' ? 'フォーマット' : 'カスタムプロンプト'}: ${tag.label}`}
        >
          {tag.icon}
          <span className="whitespace-nowrap">{tag.label}</span>
        </div>
      ))}
    </div>
  );
};
